import { InsightSummaryDTO, ChannelSummaryData } from '~/api/insights';
import {
  CampaignStatisticName,
  DateFilterType,
  InsightsContext,
  InsightsFilters,
  InsightType,
  Channel,
  ChannelColumnConfig,
  ChannelFilters,
  ChannelMetrics,
  ConversionFunnel,
  PracticeROI,
  ProgramSummary,
  PromotePageContext,
  ColumnDefinition,
} from '../_types';
import { formatCurrency } from '~/utils/format-currency';

export const INSIGHTS_DATATYPE_OPTIONS = [
  { label: 'Campaigns', value: InsightType.CAMPAIGN },
  { label: 'Ad Sets', value: InsightType.ADSET },
  { label: 'Ads', value: InsightType.AD },
];

export const PROMOTE_INSIGHTS_INJECT = Symbol('PROMOTE_INSIGHTS_INJECT');

export const DATE_FILTER_OPTIONS: { value: DateFilterType; label: string }[] = [
  { value: DateFilterType.DATE_RANGE, label: 'Date range' },
  { value: DateFilterType.TOTAL, label: 'Total' },
];

export const DEFAULT_INSIGHTS_FILTERS: InsightsFilters = {
  startDate: null,
  endDate: null,
  type: InsightType.CAMPAIGN,
  childrenOf: null,
  page: 1,
  perPage: 10,
};

export const DEFAULT_INSIGHT_SUMMARY_FILTERS: InsightsFilters = {
  startDate: null,
  endDate: null,
};

export const DEFAULT_INSIGHT_SUMMARY: ChannelSummaryData = {
  reach: 0,
  impressions: 0,
  spendInCents: 0,
  clicks: 0,
  cpcInCents: 0,
  ctrInPercentagePoints: 0,
  results: 0,
};

export const DEFAULT_INSIGHTS_CONTEXT: InsightsContext = {
  filters: { ...DEFAULT_INSIGHTS_FILTERS },
  rows: [],
  totalRowsCount: 0,
  summary: { ...DEFAULT_INSIGHT_SUMMARY },
  hierarchyPath: [],
};

export const SUMMARY_CAMPAIGNS_STATS = [
  {
    key: 'reach',
    name: CampaignStatisticName.REACH,
    title: 'Reach',
    description: 'Accounts Center',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'impressions',
    name: CampaignStatisticName.IMPRESSIONS,
    title: 'Impressions',
    description: 'Total',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'spendInCents',
    name: CampaignStatisticName.AMOUNT_SPENT,
    title: 'Amount spent',
    description: 'Total Spent',
    formatter: (value: number) =>
      value > 99_999
        ? formatCurrency(value / 100, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })
        : formatCurrency(value / 100),
  },
  {
    key: 'clicks',
    name: CampaignStatisticName.CLICKS,
    title: 'Clicks (all)',
    description: 'Total',
  },
  {
    key: 'cpcInCents',
    name: CampaignStatisticName.CPC,
    title: 'CPC (all)',
    description: 'Per Click',
    formatter: (value: number) => formatCurrency(value / 100),
  },
  {
    key: 'ctrInPercentagePoints',
    name: CampaignStatisticName.CTS,
    title: 'CTR (all)',
    description: 'Per Impressions',
    formatter: (value: number) => `${Number((value / 100).toFixed(2))}%`,
  },
];

export const PROMOTE_PAGE_INJECT = Symbol('PROMOTE_PAGE_INJECT');

export const CHANNEL_COLUMNS: ChannelColumnConfig = {
  meta: [
    { name: 'Campaign', rowKey: 'name', frozen: true },
    { name: 'Amount Spent', rowKey: 'spent' },
    { name: 'Impressions', rowKey: 'impressions' },
    { name: 'Reach', rowKey: 'reach' },
    { name: 'Results', rowKey: 'results' },
    { name: 'Cost per Result', rowKey: 'costPerResult' },
    { name: 'Link Clicks', rowKey: 'linkClicks' },
    { name: 'CPC', rowKey: 'cpc' },
    { name: 'CPM', rowKey: 'cpm' },
    { name: 'CTR', rowKey: 'ctr' },
    { name: 'End Date', rowKey: 'endDate' },
  ],
  google: [
    { name: 'Campaign', rowKey: 'name', frozen: true, label: '' },
    { name: 'Cost', rowKey: 'cost', label: '' },
    { name: 'Impressions', rowKey: 'impressions', label: '' },
    { name: 'Conversions', rowKey: 'conversions', label: '' },
    { name: 'Conv. Rate', rowKey: 'conversionRate', label: '' },
    { name: 'Clicks', rowKey: 'clicks', label: '' },
    { name: 'Avg. CPC', rowKey: 'avgCpc', label: '' },
    { name: 'Views', rowKey: 'views', label: '' },
    { name: 'Cost / Conv.', rowKey: 'costPerConversion', label: '' },
    { name: 'CTR', rowKey: 'ctr', label: '' },
  ],
  tiktok: [
    { name: 'Campaign', rowKey: 'name', frozen: true, label: '' },
    { name: 'Cost', rowKey: 'cost', label: '' },
    { name: 'Impressions', rowKey: 'impressions', label: '' },
    { name: 'Conversions', rowKey: 'conversions', label: '' },
    { name: 'Cost per Conversion', rowKey: 'costPerConversion', label: '' },
    { name: 'Clicks', rowKey: 'clicks', label: '' },
    { name: 'CPC', rowKey: 'cpc', label: '' },
    { name: 'CPM', rowKey: 'cpm', label: '' },
    { name: 'CTR', rowKey: 'ctr', label: '' },
    { name: 'Conversion Rate', rowKey: 'conversionRate', label: '' },
  ],
};

export const COMMON_COLUMNS: ColumnDefinition[] = [
  { name: 'Campaign', rowKey: 'name', frozen: true, label: '' },
  { name: 'Cost', rowKey: 'cost', label: '' },
  { name: 'Impressions', rowKey: 'impressions', label: '' },
  { name: 'Clicks', rowKey: 'clicks', label: '' },
  { name: 'Results', rowKey: 'results', label: '' },
  { name: 'CPC', rowKey: 'cpc', label: '' },
  { name: 'CTR', rowKey: 'ctr', label: '' },
  { name: 'End Date', rowKey: 'endDate', label: '' },
];

export const CHANNEL_BREADCRUMBS: Record<
  Channel,
  Record<InsightType, string>
> = {
  meta: {
    [InsightType.CAMPAIGN]: 'Campaigns',
    [InsightType.ADSET]: 'Ad Sets',
    [InsightType.AD]: 'Ads',
  },
  google: {
    [InsightType.CAMPAIGN]: 'Campaigns',
    [InsightType.ADSET]: 'Ad Groups',
    [InsightType.AD]: 'Ads',
  },
  tiktok: {
    [InsightType.CAMPAIGN]: 'Campaigns',
    [InsightType.ADSET]: 'Ad Groups',
    [InsightType.AD]: 'Ads',
  },
};

export const CHANNEL_LABELS: Record<Channel, string> = {
  meta: 'Meta',
  google: 'Google',
  tiktok: 'TikTok',
};

export const DEFAULT_PROGRAM_SUMMARY: ProgramSummary = {
  campaignCost: 0,
  totalBalance: 0,
  timeRemaining: 0,
};

export const DEFAULT_PRACTICE_ROI: PracticeROI = {
  practiceRevenue: 0,
  practiceROI: 0,
  costPerLead: 0,
  costPerConsult: 0,
  costPerProcedure: 0,
  hasData: false,
};

export const DEFAULT_CONVERSION_FUNNEL: ConversionFunnel = {
  totalLeads: 0,
  consultsBooked: 0,
  consultsCompleted: 0,
  proceduresBooked: 0,
  proceduresCompleted: 0,
  otherServices: 0,
  hasData: false,
};

export const DEFAULT_CHANNEL_FILTERS: ChannelFilters = {
  activeChannel: 'all',
  startDate: null,
  endDate: null,
};

export const DEFAULT_CHANNEL_METRICS: ChannelMetrics = {
  reach: 0,
  impressions: 0,
  leads: 0,
  costPerLead: 0,
  clicks: 0,
  costPerClick: 0,
};

export const DEFAULT_PROMOTE_PAGE_CONTEXT: PromotePageContext = {
  programSummary: { ...DEFAULT_PROGRAM_SUMMARY },
  practiceROI: { ...DEFAULT_PRACTICE_ROI },
  conversionFunnel: { ...DEFAULT_CONVERSION_FUNNEL },
  connectedChannels: {
    channels: [],
    hasData: {} as Record<Channel, boolean>,
  },
  channelFilters: { ...DEFAULT_CHANNEL_FILTERS },
  channelSummary: { ...DEFAULT_CHANNEL_METRICS },
  channelDetails: {
    rows: [],
    totalCount: 0,
    page: 1,
    perPage: 10,
  },
};

export const CHANNEL_SUMMARY_STATS = [
  {
    key: 'reach',
    title: 'Reach',
    description: 'Total',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'impressions',
    title: 'Impressions',
    description: 'Total',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'leads',
    title: 'Leads',
    description: 'Total',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'costPerLead',
    title: 'Cost per Lead',
    description: 'Average',
    formatter: (value: number) => formatCurrency(value / 100),
  },
  {
    key: 'clicks',
    title: 'Clicks',
    description: 'Total',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'costPerClick',
    title: 'Cost per Click',
    description: 'Average',
    formatter: (value: number) => formatCurrency(value / 100),
  },
];

export const FUNNEL_STAGES = [
  {
    key: 'totalLeads',
    label: 'Total Leads',
    description: 'All generated leads',
  },
  {
    key: 'consultsBooked',
    label: 'Consults Booked',
    description: 'Scheduled consultations',
  },
  {
    key: 'consultsCompleted',
    label: 'Consults Completed',
    description: 'Completed consultations',
  },
  {
    key: 'proceduresBooked',
    label: 'Procedures Booked',
    description: 'Scheduled procedures',
  },
  {
    key: 'proceduresCompleted',
    label: 'Procedures Completed',
    description: 'Completed procedures',
  },
  {
    key: 'otherServices',
    label: 'Other Services',
    description: 'Additional services',
  },
];
